import { booleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';
import {
  EnumFilter,
  enumFilter,
  enumFilterSchema
} from 'v5/api/_common/query/filters/EnumFilter';
import { stringFilter } from 'v5/api/_common/query/filters/StringFilter';
import { coreFieldFiltersSchema } from 'v5/core/core-fields/core-fields-filters.schemas';
import { CoreFieldFilters } from 'v5/core/core-fields/core-fields-filters.types';
import { withCustomFieldFiltersSchema } from 'v5/core/custom-fields/custom-fields-filters.schemas';
import { WithCustomFieldFilters } from 'v5/core/custom-fields/custom-fields-filters.types';
import { z } from 'zod';
import type { ContactCreateBody } from 'v5/core/resource-write/contactBody.types';
import { Resource, ResourceTypeModel } from '../../../__types__';
import type { ValidatedRow } from 'v5/platform/row-level-validation';
import {
  PaginatedResponse,
  paginationSchema,
  paginationUtils
} from '../_common/query/pagination';
import {
  searchUtils,
  withBuilderUtils,
  withFiltersUtils,
  WithOptionalFilters,
  WithPagination,
  withPaginationUtils,
  WithSearch,
  withSearchUtils,
  WithSorter,
  withSorterUtils
} from '../_common/query/queryUtils';
import { Overwrite } from 'v5/platform/typescript';
import { WithWorkspaceScoping } from '../_common/query/workspace-scoping';

/** Org-talents workspace filter: workspace ids plus global (`null` membership). */
export type ResourceWorkspaceFilterValue = number | null;

function resourceWorkspaceFilterTokens(
  resource: Resource
): ResourceWorkspaceFilterValue[] {
  return (resource.enterprise_workspaces ?? []).map(
    entry => entry.enterprise_workspace_id
  );
}

// qs encodes JSON null as "" in list URLs.
const resourceWorkspaceFilterValueSchema = z.preprocess(
  value => (value === '' ? null : value),
  z.union([z.null(), z.coerce.number().int()])
);

/** Built-in table column ids for resources list. */
export type ResourceBuiltinTableColumnField =
  | 'full_name'
  | 'role'
  | 'locations'
  | 'groups'
  | 'tags'
  | 'websites';

/** Table column ids including dynamic custom field columns (`custom_<id>`). */
export type ResourceTableColumnField = ResourceBuiltinTableColumnField | string;

export type ResourceSearchQuery = WithSearch &
  WithPagination &
  WithSorter<'full_name' | 'role' | 'createdAt'> &
  WithOptionalFilters<
    WithCustomFieldFilters<
      Overwrite<
        CoreFieldFilters,
        {
          resource_type_model: EnumFilter<ResourceTypeModel>;
          userId: EnumFilter<string>;
          enterprise_workspace_id: EnumFilter<ResourceWorkspaceFilterValue>;
        }
      >
    >
  >;

export type ResourceQuery = WithWorkspaceScoping<ResourceSearchQuery>;

/** Search-only query accepted by list APIs; global workspace scope is applied when omitted. */
export type ResourceListQuery = ResourceSearchQuery | ResourceQuery;

/** Paginated list: each row is validated in `createResourceApi` (resource.api.http). */
export type ResourceListResponse = PaginatedResponse<ValidatedRow<Resource>>;

export interface BulkArchiveRequest {
  ids: Resource['id'][];
}

export interface AssignResourcesToWorkspacesRequest {
  ids: Resource['id'][];
  /**
   * - [] → hidden
   * - [null] → global
   * - [1, 2, …] → workspaces
   */
  enterprise_workspace_ids: Array<number | null>;
}

export interface ResourceApi {
  getList: (
    query: ResourceQuery,
    signal?: AbortSignal
  ) => Promise<ResourceListResponse>;
  getItem: (id: Resource['id']) => Promise<Resource>;
  /** One validation result per id, so one invalid contact doesn't fail the others. */
  getItems: (ids: Resource['id'][]) => Promise<ValidatedRow<Resource>[]>;
  updateItem: (
    id: Resource['id'],
    data: Partial<Resource>
  ) => Promise<Resource>;
  /** `POST /contacts` with a graph body built by `v5/core/resource-write/buildCreateBody`. */
  createItem: (data: ContactCreateBody) => Promise<Resource>;
  assignResourcesToWorkspaces: (
    request: AssignResourcesToWorkspacesRequest
  ) => Promise<void>;
  bulkArchive: (request: BulkArchiveRequest) => Promise<void>;
  bulkUnarchive: (request: BulkArchiveRequest) => Promise<void>;
}

export const resourceQueryUtils = withBuilderUtils({
  ...withSearchUtils<ResourceSearchQuery>(),
  ...withFiltersUtils<ResourceSearchQuery>(),
  ...withPaginationUtils<ResourceSearchQuery>(),
  ...withSorterUtils<ResourceSearchQuery>(),
  ofEmptySearchQuery: (): ResourceSearchQuery => ({
    search: '',
    sort: null,
    page: paginationUtils.ofDefault(),
    filters: {}
  }),
  ofDefault: (): ResourceSearchQuery => ({
    search: '',
    sort: { field: 'full_name', order: 'asc' },
    page: paginationUtils.ofDefault(),
    filters: {
      archived: { operator: 'equal', value: false }
    }
  }),
  matchesQuery: (query: ResourceSearchQuery, resource: Resource) => {
    if (
      query.search &&
      !searchUtils.matches(query.search, resource.full_name)
    ) {
      return false;
    }
    if (
      !enumFilter.matches(
        query.filters.resource_type_id,
        resource.resource_type_id
      )
    ) {
      return false;
    }
    if (
      !enumFilter.matches(
        query.filters.organisation_id,
        resource.organisation_id ?? -1
      )
    ) {
      return false;
    }
    if (
      query.filters?.represented_by_id &&
      !enumFilter.matches(
        query.filters.represented_by_id,
        resource.represented_by_id
      )
    ) {
      return false;
    }
    if (
      query.filters?.tags &&
      !enumFilter.matchesArray(
        query.filters.tags,
        (resource.tags ?? []).map(tag => tag.tag)
      )
    ) {
      return false;
    }
    if (
      !enumFilter.matchesArray(
        query.filters.groups,
        (resource.groups ?? []).map(group => group.id)
      )
    ) {
      return false;
    }
    if (!stringFilter.matches(query.filters.role, resource.role)) {
      return false;
    }
    if (
      !enumFilter.matches(
        query.filters.resource_type_model,
        (resource as any).resource_type_model
      )
    ) {
      return false;
    }
    if (
      !booleanFilter.matches(query.filters.archived, resource.archived ?? false)
    ) {
      return false;
    }

    // Owner filter (userId filter compared to resource.user_id field)
    const userIdFilter = query.filters.userId;
    if (!enumFilter.matches(userIdFilter, resource.user_id)) {
      return false;
    }

    if (
      !enumFilter.matchesArray(
        query.filters.enterprise_workspace_id,
        resourceWorkspaceFilterTokens(resource)
      )
    ) {
      return false;
    }

    return true;
  }
}).setInitialQueryFactory(x => x.ofEmptySearchQuery);

const strToNull = (str: unknown) => (str === '' ? null : str);

export const resourceSearchQuerySchema = z
  .object({
    search: z.preprocess(value => value || '', z.string()).default(''),
    filters: z.preprocess(
      data => data ?? {},
      withCustomFieldFiltersSchema(
        coreFieldFiltersSchema.extend({
          resource_type_model: z.preprocess(
            strToNull,
            enumFilterSchema(z.string()).nullable()
          ),
          userId: z.preprocess(
            strToNull,
            enumFilterSchema(z.string()).nullable()
          ),
          enterprise_workspace_id: z.preprocess(
            strToNull,
            enumFilterSchema(resourceWorkspaceFilterValueSchema).nullable()
          )
        })
      ).partial()
    ),
    page: paginationSchema,
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(['full_name', 'role', 'createdAt']),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .default(null)
  })
  .catch(e => {
    console.error('Error parsing resource search query', e.issues);
    return resourceQueryUtils.ofEmptySearchQuery();
  });

export const resourceQuerySchema = resourceSearchQuerySchema;
